import { useEffect, useState } from "react";

import { queryClient } from "@config/queryClient";
import useCompanyStore from "@store/useCompanyStore";
import { useGetCompany } from "./hooks/useGetCompany";
import { useFindCompanyByWebsite } from "@components/AddNewEntity/hooks/useFindCompanyByWebsite";
import { addUrlProtocol, getBaseUrl } from "@utils/strings.util";
import { RelationObjectType } from "@enums/relations.enum";
import { CreateOrEditCompanySkeleton } from "./CreateOrEditCompanySkeleton";
import CreateOrEditCompany from "./CreateOrEditCompany";
import { COMPANY_QUERY_KEY } from "@config/query";

const CONTENT_TYPE = RelationObjectType.COMPANY;

export default function CompanyScreen() {
  const { companyId, setCompanyId, currentUrl } = useCompanyStore();
  const normalizedUrl = getBaseUrl(addUrlProtocol(currentUrl));
  const [prevUrl, setPrevUrl] = useState<string | null>(null);

  const { data: company, isLoading: isLoadingCompany } = useGetCompany(companyId);
  const {
    data: companyLookup,
    isLoading: isLoadingLookup,
    error: companyLookupError,
    refetch: refetchCompanies,
  } = useFindCompanyByWebsite(normalizedUrl);

  const foundCompanyId = companyLookup?.company_id ?? null;

  useEffect(() => {
    if (isLoadingLookup) return;

    if (currentUrl !== prevUrl) {
      // URL navigation — wipe stale cache and re-sync from lookup result.
      queryClient.removeQueries({ queryKey: [COMPANY_QUERY_KEY] });
      setCompanyId(foundCompanyId ?? null);
      setPrevUrl(currentUrl);
      return;
    }

    // Same URL but lookup result changed (e.g. company was just created).
    // Sync companyId without wiping the optimistic cache.
    if (foundCompanyId && foundCompanyId !== companyId) {
      setCompanyId(foundCompanyId);
    }
  }, [currentUrl, foundCompanyId, isLoadingLookup, companyId, prevUrl]);

  if (isLoadingLookup || (foundCompanyId && isLoadingCompany))
    return <CreateOrEditCompanySkeleton />;

  return (
    <CreateOrEditCompany
      company={company}
      currentUrl={normalizedUrl}
      contentType={CONTENT_TYPE}
      hasCompanyLookupError={!!companyLookupError}
      onRetryCompanyLookup={refetchCompanies}
    />
  );
}
